import { Injectable, NgZone } from "@angular/core";
import { BleClient, ScanResult } from "@capacitor-community/bluetooth-le";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { BehaviorSubject, Subject } from "rxjs";
import { PosthogService } from "./posthog.service";
import { APP_VERSION } from "../../../environments/version";
import { CoreService } from "./core.service";
import { EidTagData, HaloEidFramer, Hr0EidFramer, TextEidFramer } from "./eid";
import {
  selectReaderChannel,
  identifyReader,
  readerDisplayName,
  ReaderChannel,
  ReaderProfile,
} from "./ble-reader-profiles";

export interface ReaderState {
  status: "disconnected" | "connecting" | "ready" | "error";
  message: string;
}

interface ReaderConnection {
  device: ScanResult;
  profile: ReaderProfile;
  framer: TextEidFramer | HaloEidFramer | Hr0EidFramer;
  channel?: ReaderChannel;
  subscribed: boolean;
  closing?: boolean;
}

@Injectable({ providedIn: "root" })
export class BluetoothService {
  isScanning = false;
  devices: ScanResult[] = [];
  connectedDevice: ScanResult | null = null;
  lastReader: ScanResult | null = null;
  private active?: ReaderConnection;
  private connectionRequest = 0;
  private connectionQueue: Promise<void> = Promise.resolve();
  private scanRequest = 0;
  private nativeScanning = false;
  private scanQueue: Promise<void> = Promise.resolve();
  private scanTimer?: ReturnType<typeof setTimeout>;
  private notificationsEnabled = true;
  private readonly scanning = new BehaviorSubject(false);
  private readonly discovered = new BehaviorSubject<ScanResult[]>([]);
  private readonly connected = new BehaviorSubject<ScanResult | null>(null);
  // Scan events must never replay when an Ionic page subscribes again.
  private readonly scans = new Subject<EidTagData>();
  private readonly testScans = new Subject<EidTagData>();
  private testing = false;
  private scanContext = 0;
  private readonly state = new BehaviorSubject<ReaderState>({ status: "disconnected", message: "No reader connected" });

  readonly isScanningSubject = this.scanning.asObservable();
  readonly devicesSubject = this.discovered.asObservable();
  readonly connectedDeviceSubject = this.connected.asObservable();
  readonly connectedDeviceScanReturn = this.scans.asObservable();
  readonly testScanReturn = this.testScans.asObservable();
  readonly connectionState = this.state.asObservable();

  constructor(
    private ngZone: NgZone,
    private core: CoreService,
    private analytics: PosthogService,
  ) {}

  async loadLastReader(): Promise<void> {
    try {
      const { value } = await Preferences.get({ key: "ble-last-reader" });
      const saved = value ? JSON.parse(value) : null;
      if (typeof saved?.device?.deviceId === "string" && identifyReader(saved)) this.lastReader = saved;
    } catch {
      this.captureError("load_last_reader");
      this.lastReader = null;
    }
  }

  resetScanData(): void {
    ++this.scanContext;
    this.active?.framer.reset();
  }

  clearDevices(): void {
    this.devices = [];
    this.discovered.next([]);
  }

  async initializeBle(): Promise<void> {
    await BleClient.initialize(Capacitor.getPlatform() === "android" ? { androidNeverForLocation: true } : undefined);
    if (!(await BleClient.isEnabled())) {
      if (Capacitor.getPlatform() === "android") await BleClient.requestEnable();
      if (!(await BleClient.isEnabled()))
        throw new Error("Bluetooth is turned off. Turn it on in Settings, then try again.");
    }
  }

  isEnabled(): Promise<boolean> {
    return BleClient.isEnabled();
  }
  requestEnable(): Promise<void> {
    return BleClient.requestEnable();
  }

  scanForDevices(): Promise<void> {
    const request = ++this.scanRequest;
    clearTimeout(this.scanTimer);
    this.scanQueue = this.scanQueue
      .catch(() => {})
      .then(async () => {
        if (request !== this.scanRequest) return;
        try {
          await this.initializeBle();
          if (this.nativeScanning) await BleClient.stopLEScan();
          if (request !== this.scanRequest) return;
          this.clearDevices();
          this.setScanning(true);
          // Some readers advertise only a name and expose the EID service after connection.
          this.nativeScanning = true;
          await BleClient.requestLEScan({ allowDuplicates: false }, result => {
            if (request === this.scanRequest) this.ngZone.run(() => this.onDeviceDiscovered(result));
          });
          if (request !== this.scanRequest) return;
          this.scanTimer = setTimeout(() => {
            if (request === this.scanRequest) void this.stopScanning();
          }, 15000);
        } catch (error) {
          this.captureError("scan", undefined, undefined, this.errorReason(error));
          this.setScanning(false);
          this.reportError(
            "Cannot scan. Enable Bluetooth and allow Nearby devices or Bluetooth access in Settings, then try again.",
          );
        }
      });
    return this.scanQueue;
  }

  stopScanning(): Promise<void> {
    ++this.scanRequest;
    clearTimeout(this.scanTimer);
    this.setScanning(false);
    this.scanQueue = this.scanQueue
      .catch(() => {})
      .then(async () => {
        if (this.nativeScanning) {
          await BleClient.stopLEScan();
          this.nativeScanning = false;
        }
      })
      .catch(error => {
        this.captureError("stop_scan", undefined, undefined, this.errorReason(error));
        this.core.errorToast("Could not stop discovery. Turn Bluetooth off and on before scanning again.");
      });
    return this.scanQueue;
  }

  private setScanning(value: boolean): void {
    this.isScanning = value;
    this.scanning.next(value);
  }

  onDeviceDiscovered(device: ScanResult): void {
    const index = this.devices.findIndex(item => item.device.deviceId === device.device.deviceId);
    if (index < 0) this.devices.push(device);
    else this.devices[index] = device;
    this.devices.sort((a, b) => (b.rssi ?? -Infinity) - (a.rssi ?? -Infinity));
    this.discovered.next([...this.devices]);
  }

  connectToDevice(device: ScanResult): Promise<void> {
    if (this.connectedDevice?.device.deviceId === device.device.deviceId) return Promise.resolve();
    const request = ++this.connectionRequest;
    this.publishDevice(null);
    this.state.next({
      status: "connecting",
      message: `Connecting to ${readerDisplayName(device)}…`,
    });
    this.connectionQueue = this.connectionQueue
      .catch(() => {})
      .then(async () => {
        if (request !== this.connectionRequest) return;
        let connection: ReaderConnection | undefined;
        let operation = "initialise";
        try {
          await this.closeActive();
          await this.stopScanning();
          await this.initializeBle();
          if (request !== this.connectionRequest) return;
          operation = "identify_model";
          const profile = identifyReader(device);
          if (!profile)
            throw new Error(
              "This reader's BLE protocol is not verified. Check the exact model in Reader setup before trying again.",
            );
          connection = {
            device,
            profile,
            framer:
              profile.protocol === "halo"
                ? new HaloEidFramer(() => this.captureError("decode_record", profile, undefined, "invalid_record"))
                : profile.protocol === "hr0"
                  ? new Hr0EidFramer()
                  : new TextEidFramer(() => this.captureError("decode_record", profile, undefined, "invalid_record")),
            subscribed: false,
          };
          this.active = connection;
          const current = connection;
          operation = "connect";
          if (Capacitor.getPlatform() === "ios") {
            const devices = await BleClient.getDevices([device.device.deviceId]);
            if (request !== this.connectionRequest || this.active !== current) return;
            if (!devices.some(known => known.deviceId === device.device.deviceId))
              throw new Error("Reader not found. Turn it on and use Find nearby readers to reconnect.");
          }
          await BleClient.connect(device.device.deviceId, () =>
            this.ngZone.run(() => {
              if (this.active !== current) return;
              this.active = undefined;
              current.framer.reset();
              if (current.closing) return;
              this.captureError("unexpected_disconnect", current.profile);
              this.publishDevice(null);
              this.state.next({
                status: "disconnected",
                message: "Reader disconnected. Check its power and range, then reconnect.",
              });
            }),
          );
          if (request !== this.connectionRequest || this.active !== current) return;
          operation = "discover_services";
          const services = await BleClient.getServices(device.device.deviceId);
          current.channel = selectReaderChannel(profile, services);
          if (!current.channel)
            throw new Error(
              "No single matching EID notification service. Check the reader's BLE mode and model, then reconnect.",
            );
          if (request !== this.connectionRequest || this.active !== current) return;
          const pending: { tag: EidTagData; context: number; testing: boolean }[] = [];
          operation = "subscribe";
          await BleClient.startNotifications(
            device.device.deviceId,
            current.channel.service,
            current.channel.notify,
            value => {
              if (this.active !== current || current.closing || request !== this.connectionRequest) return;
              this.ngZone.run(() => {
                let tags: EidTagData[];
                try {
                  tags = current.framer.push(value);
                } catch {
                  this.captureError("decode_notification", current.profile, value.byteLength, "invalid_frame");
                  this.reportError(
                    "Reader packet could not be decoded. Test a known tag; if reads are missing, contact FlockFinder support with the reader model and firmware.",
                  );
                  return;
                }
                if (tags.length && current.subscribed && this.connectedDevice === current.device)
                  this.state.next({ status: "ready", message: "Ready to receive scans" });
                if (this.notificationsEnabled)
                  for (const tag of tags) {
                    if (current.subscribed) (this.testing ? this.testScans : this.scans).next(tag);
                    else pending.push({ tag, context: this.scanContext, testing: this.testing });
                  }
              });
            },
          );
          current.subscribed = true;
          if (request !== this.connectionRequest || this.active !== current) return;
          this.publishDevice(device);
          this.state.next({ status: "ready", message: "Ready to receive scans" });
          this.ngZone.run(() => {
            for (const event of pending) {
              if (event.context === this.scanContext && this.notificationsEnabled)
                (event.testing ? this.testScans : this.scans).next(event.tag);
            }
          });
          this.lastReader = device;
          try {
            await Preferences.set({
              key: "ble-last-reader",
              value: JSON.stringify({ device: device.device, localName: device.localName }),
            });
          } catch {
            this.captureError("save_last_reader", current.profile);
            this.core.errorToast("Reader connected, but its details could not be saved for reconnection.");
          }
        } catch (error) {
          if (request === this.connectionRequest) {
            this.captureError(
              operation,
              connection?.profile || identifyReader(device),
              undefined,
              this.errorReason(error),
            );
            this.reportError(
              error instanceof Error
                ? error.message
                : "Connection failed. Check Bluetooth permissions, reader power and BLE mode, then reconnect.",
            );
          }
        } finally {
          if (connection && (this.connectedDevice !== device || request !== this.connectionRequest)) {
            await this.closeConnection(connection).catch(error => this.reportError(error.message));
          }
        }
      });
    return this.connectionQueue;
  }

  disconnectFromDevice(_deviceId: string): Promise<void> {
    ++this.connectionRequest;
    this.publishDevice(null);
    this.state.next({ status: "disconnected", message: "Reader disconnected" });
    this.connectionQueue = this.connectionQueue
      .catch(() => {})
      .then(() => this.closeActive())
      .catch(error => this.reportError(error.message));
    return this.connectionQueue;
  }

  private async closeActive(): Promise<void> {
    if (this.active) await this.closeConnection(this.active);
  }

  private async closeConnection(connection: ReaderConnection): Promise<void> {
    connection.closing = true;
    connection.framer.reset();
    const id = connection.device.device.deviceId;
    try {
      if (connection.subscribed)
        await BleClient.stopNotifications(id, connection.channel.service, connection.channel.notify);
    } catch (error) {
      this.captureError("unsubscribe", connection.profile, undefined, this.errorReason(error));
      // A reader out of range may reject unsubscribe; still release the native connection.
    }
    try {
      await BleClient.disconnect(id);
      if (this.active === connection) this.active = undefined;
    } catch (error) {
      this.captureError("disconnect", connection.profile, undefined, this.errorReason(error));
      throw new Error("Could not disconnect the reader. Turn it off, then retry before connecting another reader.", {
        cause: error,
      });
    }
  }

  private publishDevice(device: ScanResult | null): void {
    this.connectedDevice = device;
    this.connected.next(device);
  }

  private errorReason(error: unknown): string {
    const message = error instanceof Error ? error.message.toLowerCase() : "";
    if (/permission|denied|unauthori[sz]ed/.test(message)) return "permission_denied";
    if (/timed? ?out|timeout/.test(message)) return "timeout";
    if (/disabled|turned off|not enabled/.test(message)) return "bluetooth_disabled";
    if (/not supported|unimplemented|unavailable/.test(message)) return "unavailable";
    return "operation_failed";
  }

  private captureError(
    operation: string,
    profile?: ReaderProfile,
    packetLength?: number,
    reason = "operation_failed",
  ): void {
    // Only allowlisted diagnostics: native error text can contain addresses or tag data.
    void this.analytics
      .capture("ble_error", {
        operation,
        reason,
        reader_model: profile?.id || "unknown",
        protocol: profile?.protocol || "unknown",
        platform: Capacitor.getPlatform(),
        app_version: APP_VERSION,
        connection_state: this.state.value.status,
        ...(packetLength === undefined ? {} : { packet_length: packetLength }),
      })
      .catch(() => {});
  }

  private reportError(message: string): void {
    this.state.next({ status: "error", message });
    this.core.errorToast(message);
  }

  setTestScanMode(enabled: boolean): void {
    this.testing = enabled;
    this.resetScanData();
  }

  async pauseNotifications(): Promise<void> {
    this.notificationsEnabled = false;
    this.resetScanData();
  }

  async resumeNotifications(): Promise<void> {
    this.resetScanData();
    this.notificationsEnabled = true;
  }
}
